import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'rebass';
import { Input, Label, Radio } from '@rebass/forms';

import classNames from 'classnames';
import helpers from 'utils/helpers';
import NiceModal from 'components/NiceModal';
import { useForm } from 'hooks/useForm';
import { useKeyValidator } from 'hooks/useKeyValidator';
import { useOnPasteBlocker } from 'hooks/useOnPasteBlocker';
import { useToastHelper } from 'hooks/useToastHelper';
import InformationTooltip from 'components/InformationTooltip';
import { useArchiveStudyMutation } from 'api/useArchiveStudyMutation';
import { useHardDeleteStudyMutation } from 'api/useHardDeleteStudyMutation';
import services from 'services/services';

const DELETE_MODE = Object.freeze({
    ARCHIVE: 'archive',
    PERMANENT: 'permanent'
});

/**
 * Modal for deleting a study with two options: archive or permanently delete.
 *
 * Archive performs a soft delete (can be restored). Permanently delete requires
 * type-in confirmation and removes the study and all associated data.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is dismissed
 * @param {number} props.studyId - ID of the study being deleted
 * @param {string} props.studyName - Name of the study being deleted
 * @param {boolean} [props.canHardDelete=true] - Whether the user can permanently delete this study
 */
export function DeleteStudyModal({ isOpen, onClose, studyId, studyName, canHardDelete = true }) {
    const [mode, setMode] = useState(DELETE_MODE.ARCHIVE);
    const lock = 'delete study permanently';
    const { register, handleSubmit, reset, watch } = useForm();
    const watchedKey = watch('key');
    const isKeyValid = useKeyValidator(watchedKey, lock);
    const blockOnPaste = useOnPasteBlocker();
    const history = useHistory();
    const { addSuccessToast, addErrorToast } = useToastHelper();

    const isPermanent = mode === DELETE_MODE.PERMANENT;

    const { mutate: archiveStudy, isPending: isArchiving } = useArchiveStudyMutation({
        onSuccess: () => {
            addSuccessToast('Study archived');
            onClose();
            history.push('/studies');
        },
        onError: error => {
            services.parseAndTrackXhrErrors(error);
            addErrorToast('Failed to archive study');
        }
    });

    const { mutate: hardDeleteStudy, isPending: isDeleting } = useHardDeleteStudyMutation({
        onSuccess: () => {
            addSuccessToast("Study deletion has been queued. You'll receive an email when it's complete.");
            onClose();
            history.push('/studies');
        },
        onError: error => {
            services.parseAndTrackXhrErrors(error);
            const message = error?.response?.data?.error || 'Failed to delete study';
            addErrorToast(message);
        }
    });

    const isSubmitting = isArchiving || isDeleting;

    const onSubmit = handleSubmit(() => {
        if (isPermanent) {
            if (!helpers.validateKey(watchedKey, lock)) return;
            hardDeleteStudy(studyId);
        } else {
            archiveStudy(studyId);
        }
    });

    const onCancel = () => {
        reset();
        setMode(DELETE_MODE.ARCHIVE);
        onClose();
    };

    return (
        <NiceModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={onCancel}
            title="Delete Study"
            overflow="visible"
        >
            <p className="color-text-secondary mb-6">
                Choose how to delete <span className="medium">{studyName}</span>:
            </p>
            <form id="delete-study-form" onSubmit={onSubmit}>
                <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">Delete mode</legend>

                    <div className="mb-4">
                        <Label htmlFor="delete-mode-archive" className="!mb-0 h-8">
                            <Radio
                                id="delete-mode-archive"
                                name="delete-mode"
                                value={DELETE_MODE.ARCHIVE}
                                checked={!isPermanent}
                                onChange={() => setMode(DELETE_MODE.ARCHIVE)}
                                className="!mr-1 !my-1.5"
                            />
                            <span className="leading-8">Archive Study</span>
                        </Label>
                        <p className="fs-body-12 color-text-secondary m-0 ml-7">
                            Soft deletes the study. It can be restored later from the archived studies list.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="delete-mode-permanent" className="!mb-0 h-8">
                            <Radio
                                id="delete-mode-permanent"
                                name="delete-mode"
                                value={DELETE_MODE.PERMANENT}
                                checked={isPermanent}
                                onChange={() => setMode(DELETE_MODE.PERMANENT)}
                                disabled={!canHardDelete}
                                className={classNames('!mr-1 !my-1.5', { 'opacity-50': !canHardDelete })}
                            />
                            <span className={classNames('leading-8', { 'opacity-50': !canHardDelete })}>
                                Permanently Delete
                            </span>
                            {!canHardDelete && (
                                <InformationTooltip
                                    id="delete-mode-permanent-tooltip"
                                    place="top"
                                    className="!m-0 !my-2 !ml-1"
                                >
                                    Only the study creator or an admin can permanently delete a study.
                                </InformationTooltip>
                            )}
                        </Label>
                        <p
                            className={classNames('fs-body-12 color-text-secondary m-0 ml-7', {
                                'opacity-50': !canHardDelete
                            })}
                        >
                            Permanently deletes the study and all associated data including screener responses, uploaded
                            files, email campaigns, and SMS records. This cannot be undone.
                        </p>
                    </div>
                </fieldset>

                {isPermanent && (
                    <div className="mt-4">
                        <p className="mb-2">
                            To continue, type "<span className="medium">{lock}</span>":
                        </p>
                        <Input
                            {...register('key')}
                            onPaste={blockOnPaste}
                            autoFocus
                            autoComplete="off"
                            placeholder={lock}
                        />
                    </div>
                )}
            </form>

            <div className="modal-actions">
                <Button type="button" onClick={onCancel} variant="secondary-gray" className="!mr-2">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant={isPermanent ? 'danger-filled' : 'danger'}
                    disabled={(isPermanent && !isKeyValid) || isSubmitting}
                    className="modal-primary !m-0"
                    form="delete-study-form"
                >
                    {isPermanent ? 'Permanently Delete Study' : 'Archive Study'}
                </Button>
            </div>
        </NiceModal>
    );
}
